import {ParamComponent} from 'panel';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {parseNumber} from 'common/util';
import {RelativeDate, TimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {Section} from 'common/components/items-menu/types';
import {ChangeEvent} from 'common/components/select/types';
import {LIMITED_ATTRIBUTION_PROJECTS} from 'common/report/constants';

import {
  DefaultMultiAttributionLookbackDays,
  MaxMultiAttributionLookbackDays,
  MinMultiAttributionLookbackDays,
  MultiAttributionLookbackSessions,
  ReducedMaxMultiAttributionLookbackDays,
} from './constants';
import {MultiAttributionLookbackCancelEvent, MultiAttributionLookbackChangeEvent} from './types';
import {template} from './template';
import css from './index.inline.styl';

type MultiAttributionLookbackState = {
  stagedLookbackValue: string;
  stagedLookbackUnit: TimeUnit;
};

type MultiAttributionLookbackParams = {
  lookbackWindow: RelativeDate;
  projectId: number;
  handleCancel?: (ev: MultiAttributionLookbackCancelEvent) => void;
  handleChange?: (ev: MultiAttributionLookbackChangeEvent) => void;
};

type MultiAttributionLookbackJsxProps = {
  params?: MultiAttributionLookbackParams;
};

export class MultiAttributionLookback extends ParamComponent<
  MultiAttributionLookbackParams,
  MultiAttributionLookbackState
> {
  get config() {
    return Object.assign(super.config, {
      css,
      template,
      defaultState: {},
      useShadowDom: true,
      params: {
        lookbackWindow: {
          type: Object,
          required: true,
        },
        projectId: Number,
        handleCancel: Function,
        handleChange: Function,
      },
      hooks: {
        preUpdate: (_newState, newParams?: MultiAttributionLookbackParams) => {
          if (newParams && !isEqual(newParams.lookbackWindow, this.params.lookbackWindow)) {
            this.handleParamsUpdate(newParams);
          }
        },
      },
    });
  }

  connectedCallback() {
    this.handleParamsUpdate(this.params);
    super.connectedCallback();
  }

  handleParamsUpdate(params: MultiAttributionLookbackParams): void {
    this.update({
      stagedLookbackValue: params.lookbackWindow.value.toString(),
      stagedLookbackUnit: params.lookbackWindow.unit,
    });
  }

  getMaxLookbackDays(): number {
    if (LIMITED_ATTRIBUTION_PROJECTS.has(this.params.projectId.toString())) {
      return ReducedMaxMultiAttributionLookbackDays;
    } else {
      return MaxMultiAttributionLookbackDays;
    }
  }

  parseStagedLookbackValue(): number | undefined {
    return parseNumber(this.state.stagedLookbackValue);
  }

  getInvalidLookbackDaysText(): string | undefined {
    if (this.state.stagedLookbackUnit !== TimeUnit.Day) {
      return undefined;
    }

    const maxDays = this.getMaxLookbackDays();
    const value = this.parseStagedLookbackValue();
    if (!Number.isInteger(value)) {
      return `Lookback days must be an integer value.`;
    } else if (value < MinMultiAttributionLookbackDays || value > maxDays) {
      return `Lookback Windows up to ${maxDays} days are supported. Please choose a value within the range to generate a result.`;
    }
    return undefined;
  }

  handleLookbackValueInput(ev): void {
    this.update({stagedLookbackValue: ev.target.value});
  }

  getLookbackUnitSections(): Array<Section<TimeUnit>> {
    return [
      {
        items: [TimeUnit.Day, TimeUnit.Session].map((timeUnit) => {
          return {
            label: capitalize(`${TimeUnitLabels[timeUnit]}s`),
            value: timeUnit,
            isSelected: timeUnit === this.state.stagedLookbackUnit,
          };
        }),
      },
    ];
  }

  handleChangeLookbackUnit(ev: ChangeEvent<TimeUnit>): void {
    const unit = ev.detail.item.value;
    const value = unit === TimeUnit.Session ? MultiAttributionLookbackSessions : DefaultMultiAttributionLookbackDays;
    this.update({
      stagedLookbackUnit: unit,
      stagedLookbackValue: value.toString(),
    });
  }

  isDefaultLookbackWindow(): boolean {
    return (
      this.state.stagedLookbackUnit === TimeUnit.Day &&
      this.parseStagedLookbackValue() === DefaultMultiAttributionLookbackDays
    );
  }

  handleClickReset() {
    this.update({
      stagedLookbackUnit: TimeUnit.Day,
      stagedLookbackValue: DefaultMultiAttributionLookbackDays.toString(),
    });
  }

  dispatchChange() {
    const event: MultiAttributionLookbackChangeEvent = new CustomEvent(`change`, {
      detail: {
        lookbackWindow: {
          value: this.parseStagedLookbackValue(),
          unit: this.state.stagedLookbackUnit,
        },
      },
    });
    this.params?.handleChange(event);
  }

  dispatchCancel() {
    const event: MultiAttributionLookbackCancelEvent = new CustomEvent(`cancel`, {});
    this.params?.handleCancel(event);
  }
}

export const MultiAttributionLookbackEl = defineAndMakeJsxFactory<MultiAttributionLookbackJsxProps>(
  `mp-multi-attribution-lookback`,
  MultiAttributionLookback,
);
export const MultiAttributionLookbackReact = panel2react<MultiAttributionLookbackJsxProps>(
  `mp-multi-attribution-lookback`,
  MultiAttributionLookback,
);
